/*
 * <product-addon-item>
 * Drives a single add-on row on the product page. Each option is a group carrying
 * [data-option-index] + [data-selected-value] wrapping a native <select>. The element
 * resolves the chosen options to a variant, keeps price / image / availability in sync,
 * and feeds the variant id to the theme's <add-to-cart> element (assets/global.js) so the
 * add goes through the theme's own cart request and opens the cart drawer.
 */
if (!customElements.get('product-addon-item')) {
  class ProductAddonItem extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.addButton = this.querySelector('add-to-cart');
      this.button = this.querySelector('.product-addons__add-button');
      this.label = this.querySelector('[data-addon-label]');
      this.priceEl = this.querySelector('[data-addon-price]');
      this.imageEl = this.querySelector('[data-addon-image]');

      this.addText = this.dataset.addText || (this.label && this.label.textContent.trim()) || 'Add to Cart';
      this.addedText = this.dataset.addedText || 'Added';
      this.soldOutText = this.dataset.soldoutText || 'Sold out';

      this.optionGroups = Array.from(this.querySelectorAll('[data-option-index]')).sort(
        (a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex)
      );

      this.variants = this.parseJSON('[data-addon-variants]', []);
      this.prices = this.parseJSON('[data-addon-prices]', {});
      this.images = this.parseJSON('[data-addon-images]', {});

      this.onSelectChange = this.onSelectChange.bind(this);
      this.onAddClick = this.onAddClick.bind(this);
      this.onAdded = this.onAdded.bind(this);
      this.onError = this.onError.bind(this);

      this.querySelectorAll('.product-addons__select').forEach((select) => {
        select.addEventListener('change', this.onSelectChange);
      });

      if (this.addButton) this.addButton.addEventListener('click', this.onAddClick);
      document.addEventListener('ajaxProduct:added', this.onAdded);
      document.addEventListener('ajaxProduct:error', this.onError);

      this.onChange();
    }

    disconnectedCallback() {
      document.removeEventListener('ajaxProduct:added', this.onAdded);
      document.removeEventListener('ajaxProduct:error', this.onError);
      clearTimeout(this.resetTimer);
    }

    parseJSON(selector, fallback) {
      const el = this.querySelector(selector);
      if (!el) return fallback;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return fallback;
      }
    }

    onSelectChange(event) {
      const select = event.currentTarget;
      const group = select.closest('[data-option-index]');
      if (!group) return;

      group.dataset.selectedValue = select.value;
      const valueLabel = group.querySelector('[data-addon-option-label]');
      if (valueLabel) valueLabel.textContent = select.value;

      this.onChange();
    }

    getSelectedVariant() {
      if (!this.optionGroups.length) {
        return (
          this.variants.find(
            (variant) => String(variant.id) === String(this.addButton && this.addButton.dataset.variantId)
          ) || this.variants[0]
        );
      }

      const chosen = this.optionGroups.map((group) => group.dataset.selectedValue);
      return this.variants.find((variant) =>
        chosen.every((value, index) => String(variant.options[index]) === String(value))
      );
    }

    onChange() {
      if (!this.addButton) return;
      const variant = this.getSelectedVariant();

      if (variant && this.imageEl && this.images[variant.id]) {
        this.imageEl.src = this.images[variant.id];
        this.imageEl.removeAttribute('srcset');
      }

      if (variant && this.priceEl && this.prices[variant.id] != null) {
        this.priceEl.innerHTML = this.prices[variant.id];
      }

      clearTimeout(this.resetTimer);
      this.addButton.classList.remove('is-added');

      if (variant && variant.available) {
        this.addButton.dataset.variantId = variant.id;
        if (this.button) this.button.removeAttribute('disabled');
        if (this.label) this.label.textContent = this.addText;
      } else {
        // An empty id makes the theme's <add-to-cart> a no-op if the click ever lands.
        this.addButton.dataset.variantId = '';
        if (this.button) this.button.setAttribute('disabled', 'disabled');
        if (this.label) this.label.textContent = this.soldOutText;
      }
    }

    onAddClick() {
      this.pending = !!(this.addButton && this.addButton.dataset.variantId);
    }

    onAdded() {
      if (!this.pending) return;
      this.pending = false;
      if (!this.label) return;

      this.addButton.classList.add('is-added');
      this.label.textContent = this.addedText;

      clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(() => {
        this.addButton.classList.remove('is-added');
        this.label.textContent = this.addText;
      }, 2000);
    }

    onError() {
      this.pending = false;
    }
  }

  customElements.define('product-addon-item', ProductAddonItem);
}
